import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';

/**
 * Demo-mode flag + user-controllable Settings (Phase 2). All filtering is
 * frontend-driven: the backend serves the full SPC payloads and the dashboard
 * re-filters using this slice's state.
 *
 * - `isDemo` — true while the demo dashboard is active. Routes, RTK Query
 *   skips, and the mutation guard all read this.
 * - `startTimestamp` / `endTimestamp` clip the visible window on the main
 *   portfolio chart (and the per-sub detail clip).
 * - `excludedBaseAssets` is the list of demo subscriptions toggled off — by
 *   default the outliers (assets weighing >= 50% of total portfolio value) are
 *   auto-excluded so the dashboard isn't dominated by one position.
 * - `hasInitialized` flips to true after the first auto-population so a
 *   subsequent settings change isn't clobbered by the auto-default.
 *
 * Persisted via redux-persist's root config (no per-slice opt-out). Keeping
 * `isDemo` here instead of on `state.user` means user.ts has zero awareness of
 * demo mode.
 */
interface DemoState {
  isDemo: boolean;
  startTimestamp: number | null;
  endTimestamp: number | null;
  excludedBaseAssets: string[];
  hasInitialized: boolean;
}

const initialState: DemoState = {
  isDemo: false,
  startTimestamp: null,
  endTimestamp: null,
  excludedBaseAssets: [],
  hasInitialized: false,
};

const demoSlice = createSlice({
  name: 'demo',
  initialState,
  reducers: {
    enterDemo: (state) => {
      state.isDemo = true;
    },
    exitDemo: (state) => {
      state.isDemo = false;
    },
    setDemoStartTimestamp: (state, action: PayloadAction<number | null>) => {
      state.startTimestamp = action.payload;
    },
    setDemoEndTimestamp: (state, action: PayloadAction<number | null>) => {
      state.endTimestamp = action.payload;
    },
    toggleDemoBaseAsset: (state, action: PayloadAction<string>) => {
      const baseAsset = action.payload;
      const idx = state.excludedBaseAssets.indexOf(baseAsset);
      if (idx >= 0) {
        state.excludedBaseAssets.splice(idx, 1);
      } else {
        state.excludedBaseAssets.push(baseAsset);
      }
      state.hasInitialized = true;
    },
    initializeExcludedBaseAssets: (state, action: PayloadAction<string[]>) => {
      if (state.hasInitialized) return;
      state.excludedBaseAssets = action.payload;
      state.hasInitialized = true;
    },
    /**
     * Additive: ensure every base asset in the payload is in the excluded
     * list, without removing anything that's already there. Used to "re-detect
     * outliers" when the date window changes — newly-too-big assets get
     * auto-disabled but previously-disabled ones never come back on.
     */
    addToDemoExcluded: (state, action: PayloadAction<string[]>) => {
      const existing = new Set(state.excludedBaseAssets);
      for (const baseAsset of action.payload) {
        if (!existing.has(baseAsset)) {
          state.excludedBaseAssets.push(baseAsset);
          existing.add(baseAsset);
        }
      }
      state.hasInitialized = true;
    },
    resetDemoSettings: (state) => {
      // Preserve the active isDemo flag so toggling settings off doesn't kick
      // the user out of demo mode.
      return { ...initialState, isDemo: state.isDemo };
    },
  },
});

export const {
  enterDemo,
  exitDemo,
  setDemoStartTimestamp,
  setDemoEndTimestamp,
  toggleDemoBaseAsset,
  initializeExcludedBaseAssets,
  addToDemoExcluded,
  resetDemoSettings,
} = demoSlice.actions;

export const selectIsDemo = (state: RootState) => state.demo.isDemo === true;
export const selectDemoStartTimestamp = (state: RootState) =>
  state.demo.startTimestamp;
export const selectDemoEndTimestamp = (state: RootState) =>
  state.demo.endTimestamp;
export const selectDemoExcludedBaseAssets = (state: RootState) =>
  state.demo.excludedBaseAssets;
export const selectDemoHasInitialized = (state: RootState) =>
  state.demo.hasInitialized;

export default demoSlice.reducer;
